declare global {
  interface Window {
    fbq: any
    _fbq: any
  }
}

const PIXEL_ID = '636997092631981'

export function initPixel() {
  if (window.fbq) return

  const n: any = (window.fbq = function () {
    n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments)
  })

  if (!window._fbq) window._fbq = n
  n.push = n
  n.loaded = true
  n.version = '2.0'
  n.queue = []

  const script = document.createElement('script')
  script.async = true
  script.src = 'https://connect.facebook.net/en_US/fbevents.js'
  const first = document.getElementsByTagName('script')[0]
  first?.parentNode?.insertBefore(script, first)

  // Noscript fallback pixel
  const img = document.createElement('img')
  img.height = 1
  img.width = 1
  img.style.display = 'none'
  img.src = `https://www.facebook.com/tr?id=${PIXEL_ID}&ev=PageView&noscript=1`
  document.body.appendChild(img)

  window.fbq('init', PIXEL_ID)
  window.fbq('track', 'PageView')
}

// ── STANDARD EVENTS ──

// Fire Lead event — on form submission
export function trackLead(data?: { vehicle?: string; credit?: string }) {
  if (!window.fbq) return
  window.fbq('track', 'Lead', {
    content_name: 'Vehicle Inquiry',
    content_category: 'Auto Sales',
    ...(data?.vehicle && { content_type: data.vehicle }),
    ...(data?.credit && { value: data.credit }),
  })
}

// Fire Contact event — on WhatsApp clicks
export function trackContact(source: string) {
  if (!window.fbq) return
  window.fbq('track', 'Contact', {
    content_name: 'WhatsApp',
    content_category: source,
  })
}

// Fire ViewContent — when key sections come into view
export function trackViewContent(content: string) {
  if (!window.fbq) return
  window.fbq('track', 'ViewContent', {
    content_name: content,
  })
}

// Fire InitiateCheckout — when user starts filling form (progressive disclosure)
export function trackInitiateCheckout() {
  if (!window.fbq) return
  window.fbq('track', 'InitiateCheckout', {
    content_name: 'Lead Form Started',
    content_category: 'Auto Sales',
  })
}

// ── CUSTOM EVENTS ──

function trackCustom(event: string, data?: Record<string, string>) {
  if (!window.fbq) return
  window.fbq('trackCustom', event, data)
}

// ── ADVANCED TRACKING (auto-init) ──

export function initAdvancedTracking() {
  // Prevent double-init
  if ((window as any).__advancedTrackingInit) return
  ;(window as any).__advancedTrackingInit = true

  // ─── TIME ON PAGE ───
  const timeMilestones = [
    { seconds: 15, event: 'TimeOnPage_15s' },
    { seconds: 30, event: 'TimeOnPage_30s' },
    { seconds: 60, event: 'TimeOnPage_60s' },
    { seconds: 120, event: 'TimeOnPage_120s' },
  ]
  const firedTime = new Set<string>()

  let elapsedSeconds = 0
  const timeInterval = setInterval(() => {
    elapsedSeconds++
    for (const m of timeMilestones) {
      if (elapsedSeconds >= m.seconds && !firedTime.has(m.event)) {
        firedTime.add(m.event)
        trackCustom(m.event, { seconds: String(m.seconds) })
      }
    }
    // Stop checking after all milestones fired
    if (firedTime.size >= timeMilestones.length) {
      clearInterval(timeInterval)
    }
  }, 1000)

  // ─── SCROLL DEPTH ───
  const scrollMilestones = [25, 50, 75, 100]
  const firedScroll = new Set<number>()

  const handleScroll = () => {
    const scrollTop = window.scrollY || document.documentElement.scrollTop
    const docHeight =
      document.documentElement.scrollHeight -
      document.documentElement.clientHeight
    if (docHeight <= 0) return
    const percent = Math.round((scrollTop / docHeight) * 100)

    for (const milestone of scrollMilestones) {
      if (percent >= milestone && !firedScroll.has(milestone)) {
        firedScroll.add(milestone)
        trackCustom(`Scroll_${milestone}`, { depth: `${milestone}%` })
      }
    }

    if (firedScroll.size >= scrollMilestones.length) {
      window.removeEventListener('scroll', handleScroll)
    }
  }

  window.addEventListener('scroll', handleScroll, { passive: true })

  // ─── SECTION VISIBILITY (ViewContent) ───
  const sectionIds = [{ id: 'lead-form', name: 'Lead Form' }]

  // Also observe sections by tag order
  const sectionSelectors = [
    { selector: '[data-track="comparison"]', name: 'Comparison Section' },
    { selector: '[data-track="social-proof"]', name: 'Social Proof' },
    { selector: '[data-track="how-it-works"]', name: 'How It Works' },
  ]

  const firedSections = new Set<string>()

  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          const name = (entry.target as HTMLElement).dataset.trackName
          if (name && !firedSections.has(name)) {
            firedSections.add(name)
            trackViewContent(name)
            observer.unobserve(entry.target)
          }
        }
      }
    },
    { threshold: 0.3 },
  )

  // Observe by ID
  for (const s of sectionIds) {
    const el = document.getElementById(s.id)
    if (el) {
      el.dataset.trackName = s.name
      observer.observe(el)
    }
  }

  // Observe by data-track attribute
  for (const s of sectionSelectors) {
    const el = document.querySelector(s.selector)
    if (el) {
      ;(el as HTMLElement).dataset.trackName = s.name
      observer.observe(el)
    }
  }

  // ─── CTA CLICK TRACKING ───
  document.addEventListener('click', (e) => {
    const target = e.target as HTMLElement
    const cta = target.closest('[data-track-cta]') as HTMLElement | null
    if (cta) {
      const ctaName = cta.dataset.trackCta
      if (ctaName) {
        trackCustom('CTA_Click', { cta_name: ctaName })
      }
    }
  })

  // ─── PAGE VISIBILITY (detect tab switches) ───
  let hiddenAt = 0
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) {
      hiddenAt = Date.now()
    } else if (hiddenAt > 0) {
      const awaySeconds = Math.round((Date.now() - hiddenAt) / 1000)
      if (awaySeconds > 5) {
        trackCustom('TabReturn', { away_seconds: String(awaySeconds) })
      }
      hiddenAt = 0
    }
  })

  // ─── EXIT INTENT (desktop only) ───
  let exitFired = false
  document.addEventListener('mouseout', (e) => {
    if (exitFired) return
    if (e.clientY <= 0 && e.relatedTarget === null) {
      exitFired = true
      trackCustom('ExitIntent')
    }
  })
}
